import { Component, computed, inject } from '@angular/core';
import { AssetsStore } from '../stores/assets.store';
import { AssetAnalysisItem } from '../services/api';
import { ParagraphLoader } from '../paragraph-loader/paragraph-loader';
import { TextCleanPipe } from '../pipes/text-clean-pipe';
import { ThemeService } from '../services/theme';
import { ResponsiveService } from '../services/responsive';
import { v3LayoutFlags } from '../services/v3-layout';
import { V3FinnetContentGrid } from '../v3-finnet-content-grid/v3-finnet-content-grid';
import { V3ZeroContentGrid } from '../v3-zero-content-grid/v3-zero-content-grid';
import { formatAnalysisText, mapCompanyInsights } from '../services/format-analysis-text';
import { trackContentRead } from '../services/content-read';

// V3's version of the company (action) tab — same underlying data/content as V2's
// AnalyseBody, restyled into individually-carded sections. Zero and Finnet share this
// component (content/loading logic is identical) but branch template structure — see
// v3-company-profile/CLAUDE.md. On finnetWeb desktop, V3Root separately wraps this
// component's output in V3FinnetSubpageCard — this component itself stays unaware of that,
// it only needs isFinnetDesktop() to pick which internal layout to render (see below).
@Component({
  selector: 'app-v3-company-profile',
  templateUrl: './v3-company-profile.html',
  styleUrl: './v3-company-profile.scss',
  standalone: true,
  imports: [ParagraphLoader, TextCleanPipe, V3FinnetContentGrid, V3ZeroContentGrid],
})
export class V3CompanyProfile {
  private readonly themeService = inject(ThemeService);
  private readonly responsiveService = inject(ResponsiveService);

  // finnetWeb desktop only — finnetApp is Flutter-embedded, mobile-only in practice, so
  // its desktop case is unreachable but still explicitly excluded rather than assumed
  // (same convention as core.ts's showFinnetTabNavigation).
  private readonly layout = v3LayoutFlags(this.themeService, this.responsiveService);
  readonly isFinnet = this.layout.isFinnet;
  readonly isFinnetDesktop = this.layout.isFinnetDesktop;
  readonly isZeroDesktop = this.layout.isZeroDesktop;

  private readonly assetsStore = inject(AssetsStore);
  private readonly v2Data = this.assetsStore.v2Data;
  readonly loading = this.assetsStore.loading;

  private readonly generalAnalysis = computed<AssetAnalysisItem | null>(() => {
    const data = this.v2Data();
    if (!data) return null;
    const item = data.analyses.find((a) => a.analysis_type === 'general');
    if (!item || item.output_type === 'cache_miss') return null;
    return item;
  });

  readonly sections = computed(() => mapCompanyInsights(this.generalAnalysis()?.insights));

  readonly formatText = formatAnalysisText;

  constructor() {
    // Same "content read" event as V2's AnalyseBody (AI-615).
    trackContentRead(() => ({
      element_title: 'asset_analysis_read',
      feature_name: 'content_read',
    }));
  }
}
